// Wave 3b (2026-11-04): the guided tour on screen. A welcome card offers it to a new account (api: no projects yet);
// once started, a small coach card says the step (with a step indicator, Skip step and End tour), a ring points at the
// control for it (the talk light, the character card, the place, the voice picker, the make button), and the free
// Director minutes left show while they last. Every step works by voice or by tap: the api moves the step on as the
// tour's video gets each part (api/src/tour.ts), whoever did it. The last card offers a second scene.
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { director } from "../director";
import { findTarget, flash } from "../highlight";
import { unlockMedia } from "../media";
import { openPacks } from "../packs";
import { navigate, useLocation } from "../router";
import { get, markActivity, toast, useRefresh, useStore } from "../store";
import { freeClock, freeLeftDuringCall, nextStepOf, ringBox, sheetPriceLine, showsOn, stepCopy, stepNumber, type TourState } from "../tour";
import { Icon } from "./bits";
import { isLive } from "./Talk";

const POLL_MS = 8000;

/** Start (or restart) the tour from anywhere (Home, Settings > Help): the tour's video opens, and with `talk` the call. */
export async function startTour(opts: { talk?: boolean } = {}): Promise<TourState | null> {
  // inside the tap: unlock audio before anything is awaited, so the Director can be heard
  if (opts.talk) {
    unlockMedia();
    markActivity();
  }
  try {
    const t = await api.tourStart();
    tourBus.set(t);
    if (t.projectId) navigate(`/p/${t.projectId}`);
    const live = isLive(get().director);
    if (live) director.context("[app] The user just started the guided tour (see the Guided tour section). Carry on with its first step in a sentence.");
    else if (opts.talk) void director.start();
    return t;
  } catch {
    toast("The tour couldn't start just now. Try again in a moment.", "bad");
    return null;
  }
}

// the tour state shared by the host and the start buttons (a tiny store: one value, subscribers)
const tourBus = (() => {
  let value: TourState | null = null;
  const subs = new Set<(t: TourState | null) => void>();
  return {
    get: () => value,
    set(t: TourState | null) {
      value = t;
      for (const f of subs) f(t);
    },
    sub(f: (t: TourState | null) => void) {
      subs.add(f);
      return () => void subs.delete(f);
    },
  };
})();

function useTour(): [TourState | null, (t: TourState | null) => void] {
  const [t, setT] = useState<TourState | null>(tourBus.get());
  useEffect(() => tourBus.sub(setT), []);
  return [t, tourBus.set];
}

/** The ring around the step's control: follows it as the page scrolls; hidden when nothing matches. */
function Ring({ targets }: { targets: string[] }) {
  const [box, setBox] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const key = targets.join("|");
  useEffect(() => {
    let raf = 0;
    let scrolled = false;
    let seen: HTMLElement | null = null;
    const place = () => {
      raf = 0;
      let el: HTMLElement | null = null;
      for (const sel of targets) if ((el = findTarget(document, sel))) break;
      if (!el) return setBox(null);
      // a new control (another screen, or the step's own control showed up): bring it into view once
      if (el !== seen) {
        seen = el;
        scrolled = false;
      }
      let r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return setBox(null);
      // once per step: bring the control to the middle of the screen (whichever box scrolls; on phones the card sits
      // at the top and the Director bar and tabs at the bottom, so the edges don't count as in view)
      if (!scrolled && (r.bottom < 0 || r.top < 120 || r.bottom > window.innerHeight * 0.75)) {
        scrolled = true;
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch {
          el.scrollIntoView();
        }
        return setBox(null);
      }
      scrolled = true;
      r = el.getBoundingClientRect();
      // off screen (scrolled away): no ring squeezed against the edge
      if (r.bottom <= 0 || r.top >= window.innerHeight) return setBox(null);
      setBox(ringBox(r, window.innerWidth, window.innerHeight));
    };
    const soon = () => {
      if (!raf) raf = window.requestAnimationFrame(place);
    };
    place();
    const t = window.setInterval(place, 500);
    window.addEventListener("scroll", soon, true);
    window.addEventListener("resize", soon);
    return () => {
      window.clearInterval(t);
      window.removeEventListener("scroll", soon, true);
      window.removeEventListener("resize", soon);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!box) return null;
  return <div className="tour-ring" aria-hidden style={{ top: box.top, left: box.left, width: box.width, height: box.height }} />;
}

/** The free Director time left: live during a call (the call's own allowance), else what the account has. */
function useFreeLeft(t: TourState): number {
  const call = useStore((s) => s.directorCall);
  const status = useStore((s) => s.director);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!call) return;
    const i = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(i);
  }, [call]);
  if (call && isLive(status) && call.freeSeconds) return freeLeftDuringCall(call.freeSeconds, (now - call.startedAt) / 1000) ?? 0;
  return t.freeSecondsLeft;
}

export function TourHost() {
  const { path } = useLocation();
  const meTour = useStore((s) => s.me?.tour ?? null);
  const balance = useStore((s) => s.balance);
  const status = useStore((s) => s.director);
  const tick = useRefresh("project", "projects", "me", "credits", "tour");
  const [t, setT] = useTour();
  const [small, setSmall] = useState(false);
  const [busy, setBusy] = useState(false);
  const wanted = !!(meTour?.active || meTour?.offer || t?.active);

  // load when /api/me says there's a tour to show; again when the project or the balance changes, and every few
  // seconds while it's on (a sheet or a place lands from a job)
  useEffect(() => {
    if (!wanted) return;
    let live = true;
    const load = () => api.tour().then((x) => live && setT(x)).catch(() => undefined);
    void load();
    const i = t?.active ? window.setInterval(load, POLL_MS) : null;
    return () => {
      live = false;
      if (i) window.clearInterval(i);
    };
  }, [wanted, tick, t?.active]); // eslint-disable-line react-hooks/exhaustive-deps

  // a new step: tell the Director (if it's on a call) and open the card again
  const lastStep = useRef<string | null>(null);
  useEffect(() => {
    if (!t?.active) return;
    if (lastStep.current && lastStep.current !== t.step) {
      setSmall(false);
      if (isLive(get().director)) director.context(`[app] Guided tour: on to the ${t.step} step.`);
    }
    lastStep.current = t.step;
  }, [t?.active, t?.step]);

  if (!t) return null;
  const act = async (fn: () => Promise<TourState>) => {
    setBusy(true);
    try {
      setT(await fn());
    } catch {
      toast("That didn't go through. Try again in a moment.", "bad");
    } finally {
      setBusy(false);
    }
  };

  if (!t.active && t.offer && path === "/") return <Welcome t={t} busy={busy} onNo={() => void act(() => api.tourDismiss())} />;
  if (!t.active || !showsOn(path, t.step, t.projectId)) return null;
  return (
    <>
      {!small && <Ring targets={stepCopy(t.step, t).targets} />}
      {t.step === "done" ? (
        <SecondScene t={t} busy={busy} onDone={(yes) => void act(async () => {
          const x = await api.tourComplete();
          if (yes) addSecondScene(t);
          else toast("Nice work. Your scene is saved with your videos.", "good");
          return x;
        })} />
      ) : (
        <Coach t={t} balance={balance} live={isLive(status)} small={small} setSmall={setSmall} busy={busy} onSkip={() => void act(() => api.tourStep(nextStepOf(t.step)))} onEnd={() => void act(() => api.tourDismiss())} />
      )}
    </>
  );
}

function addSecondScene(t: TourState) {
  if (isLive(get().director)) return void director.send("Let's add a second scene");
  if (t.projectId) navigate(`/p/${t.projectId}`);
  // point at Add scene once the video is on screen
  let n = 0;
  const look = () => {
    const el = findTarget(document, '[data-action="scene.add"]');
    if (el) return flash(el);
    if (++n < 16) window.setTimeout(look, 200);
  };
  window.setTimeout(look, 200);
}

function Welcome({ t, busy, onNo }: { t: TourState; busy: boolean; onNo: () => void }) {
  const [going, setGoing] = useState(false);
  const go = (talk: boolean) => {
    setGoing(true);
    void startTour({ talk }).finally(() => setGoing(false));
  };
  const c = stepCopy("welcome", t);
  return (
    <section className="tour-card tour-welcome" role="dialog" aria-labelledby="tour-welcome-h" aria-describedby="tour-welcome-p">
      <p className="tour-kicker">Guided tour</p>
      <h2 id="tour-welcome-h" className="tour-title">{c.title}</h2>
      <p id="tour-welcome-p" className="tour-body">{c.body}</p>
      <ol className="tour-path" aria-label="The steps">
        <li>A character</li>
        <li>A place</li>
        <li>A voice</li>
        <li>A line</li>
        <li>Your scene</li>
      </ol>
      <p className="tour-free">
        <Icon name="mic" size={15} /> Your first {Math.round(t.freeSeconds / 60)} minutes with the Director are free.
      </p>
      <div className="tour-actions">
        <button type="button" className="btn btn-primary" disabled={going || busy} onClick={() => go(true)}>
          Start with my Director
        </button>
        <button type="button" className="btn btn-quiet" disabled={going || busy} onClick={() => go(false)}>
          I'll tap through it
        </button>
        <button type="button" className="tour-link" disabled={going || busy} onClick={onNo}>
          No thanks
        </button>
      </div>
    </section>
  );
}

function Coach({ t, balance, live, small, setSmall, busy, onSkip, onEnd }: { t: TourState; balance: number | null; live: boolean; small: boolean; setSmall: (v: boolean) => void; busy: boolean; onSkip: () => void; onEnd: () => void }) {
  const c = stepCopy(t.step, t);
  const n = stepNumber(t.step);
  const free = useFreeLeft(t);
  const price = t.step === "character" ? sheetPriceLine(balance, t.sheetCredits) : null;
  const { path } = useLocation();
  const away = !!t.projectId && !path.startsWith(`/p/${t.projectId}`);
  if (small)
    return (
      <button type="button" className="tour-pill" onClick={() => setSmall(false)} aria-label={`Guided tour${n ? `, step ${n.n} of ${n.of}` : ""}: ${c.title}. Open`}>
        <span className="tour-pill-dot" aria-hidden />
        Tour{n ? ` · ${n.n} of ${n.of}` : ""}
      </button>
    );
  return (
    <section className="tour-card tour-coach" role="region" aria-label="Guided tour" aria-live="polite">
      <header className="tour-head">
        {n && (
          <span className="tour-steps" aria-label={`Step ${n.n} of ${n.of}`}>
            {Array.from({ length: n.of }, (_, i) => (
              <span key={i} className={`tour-step-dot${i < n.n - 1 ? " is-done" : i === n.n - 1 ? " is-on" : ""}`} />
            ))}
            <span className="tour-step-n">
              {n.n} of {n.of}
            </span>
          </span>
        )}
        <button type="button" className="icon-btn tour-min" onClick={() => setSmall(true)} aria-label="Make the tour card small">
          <Icon name="expand" size={16} />
        </button>
      </header>
      <h2 className="tour-title">{c.title}</h2>
      <p className="tour-body">{c.body}</p>
      {price && (
        <p className={`tour-price${price.short ? " is-short" : ""}`}>
          {price.text}
          {price.short && (
            <button type="button" className="btn btn-primary btn-small" onClick={() => openPacks({ reason: "tour", note: price.text, source: "tour" })}>
              <Icon name="credits" size={15} /> Get credits
            </button>
          )}
        </p>
      )}
      {live && (
        <div className="tour-say">
          <button type="button" className="chip" onClick={() => void director.send(c.say)}>
            “{c.say}”
          </button>
        </div>
      )}
      <p className="tour-free">{free > 0 ? <>{freeClock(free)} of free Director time left</> : <>Your free Director minutes are used up. Tapping stays free.</>}</p>
      <div className="tour-actions">
        {away && (
          <button type="button" className="btn btn-primary btn-small" onClick={() => navigate(`/p/${t.projectId}`)}>
            Go to my scene
          </button>
        )}
        <button type="button" className="btn btn-quiet btn-small" disabled={busy} onClick={onSkip}>
          Skip this step
        </button>
        <button type="button" className="tour-link" disabled={busy} onClick={onEnd}>
          End the tour
        </button>
      </div>
    </section>
  );
}

function SecondScene({ t, busy, onDone }: { t: TourState; busy: boolean; onDone: (yes: boolean) => void }) {
  const c = stepCopy("done", t);
  return (
    <section className="tour-card tour-done" role="dialog" aria-labelledby="tour-done-h">
      <p className="tour-kicker">Guided tour · done</p>
      <h2 id="tour-done-h" className="tour-title">{c.title}</h2>
      <p className="tour-body">{c.body}</p>
      <div className="tour-actions">
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => onDone(true)}>
          <Icon name="plus" size={16} /> Add a second scene
        </button>
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => onDone(false)}>
          No, I'm done
        </button>
      </div>
    </section>
  );
}

/** "Take the guided tour" for Home and Settings > Help (anyone can start it, any time). */
export function TourButton({ className = "" }: { className?: string }) {
  const [t] = useTour();
  const [busy, setBusy] = useState(false);
  if (t?.active || t?.offer) return null;
  return (
    <button type="button" className={`btn btn-quiet btn-small tour-start ${className}`} disabled={busy} onClick={() => (setBusy(true), void startTour({ talk: false }).finally(() => setBusy(false)))}>
      <Icon name="film" size={15} /> Take the guided tour
    </button>
  );
}
